import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Link,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { LockKeyhole } from 'lucide-react';
import { publicAuthMessage, signOutOperator } from '../../auth/operator-auth.ts';
import {
  operatorCustomerIdQueryOptions,
  operatorEmailQueryOptions,
  useOperatorSessionRenewal,
} from '../../auth/operator-session-query.ts';
import { DeploymentGate } from '../../deployment/deployment-gate.tsx';
import { EnvironmentBadge } from '../../deployment/environment/environment-badge.tsx';
import { browserHttp } from '../../runtime/power-admin-api.ts';
import { developerOnlyRedirect } from '../../permissions/session-permissions.ts';
import { MainNav, navIconSize } from '../../ui/main-nav.tsx';

export type RouterContext = { queryClient: QueryClient };

// The pages open without a session: the login, and the invitation acceptance (FNSTPWR-118).
const PUBLIC_PATHS = ['/login', '/convite'] as const;
const isPublic = (pathname: string): boolean =>
  PUBLIC_PATHS.some((path) => pathname.startsWith(path));

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    if (isPublic(location.pathname)) {
      return {};
    }
    const email = await context.queryClient.query(operatorEmailQueryOptions);
    if (email.isErr()) {
      return redirect({ to: '/login' });
    }
    const customer = await context.queryClient.query(operatorCustomerIdQueryOptions);
    if (customer.isErr()) {
      return redirect({ to: '/login' });
    }
    if (await developerOnlyRedirect(context.queryClient, location.pathname)) {
      return redirect({ to: '/primeiros-passos' });
    }
    return {};
  },
  component: RootLayout,
});

// A full load of the login, so no cached session read outlives the session.
const sendToLogin = (): void => {
  window.location.assign('/login');
};

const signOutAndRedirect = (): void => {
  void (async (): Promise<void> => {
    const signedOut = await signOutOperator(browserHttp());
    // A failed sign-out leaves the session cookie live, so /login would bounce
    // straight back; the operator is told instead of silently redirected.
    if (signedOut.isErr()) {
      window.alert(`Não foi possível sair: ${publicAuthMessage(signedOut.error)}`);
      return;
    }
    window.location.assign('/login');
  })();
};

function RootLayout(): JSX.Element {
  const operatorEmailQuery = useQuery(operatorEmailQueryOptions);
  const signedInEmail =
    operatorEmailQuery.status === 'success' && operatorEmailQuery.data.isOk()
      ? operatorEmailQuery.data.value
      : '';
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  useOperatorSessionRenewal(!isPublic(pathname), sendToLogin);
  if (isPublic(pathname)) {
    return (
      <main className="admin-login-shell">
        <Outlet />
      </main>
    );
  }

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <span className="admin-brand-mark" aria-hidden="true">
            F
          </span>
          <div className="admin-brand-text">
            <strong>Finnest Power</strong>
            <span>Plano de controle regulado</span>
          </div>
        </div>
        <MainNav />
        <div className="admin-sidebar-posture" aria-label="Postura de segurança">
          <LockKeyhole aria-hidden="true" size={navIconSize} strokeWidth={2} />
          <div className="admin-brand">
            <strong>Postura bancária</strong>
            <span>
              mTLS, FAPI, consentimento e evidências de auditoria permanecem inspecionáveis.
            </span>
          </div>
        </div>
      </aside>
      <header className="admin-topbar">
        <div>
          <span className="admin-runtime-label">Operação da plataforma</span>
          <strong>Finnest Power Admin</strong>
          <p>Conecte, controle e execute suas operações de Open Finance e Open Insurance.</p>
        </div>
        <div className="admin-topbar-primary">
          <EnvironmentBadge />
          <div className="admin-topbar-operator" aria-label="Sessão do operador">
            {signedInEmail === '' ? (
              <Link to="/login">Entrar</Link>
            ) : (
              <>
                <span>{signedInEmail}</span>
                <button
                  className="fui-button fui-button-ghost"
                  onClick={signOutAndRedirect}
                  type="button"
                >
                  Sair
                </button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="admin-main">
        <DeploymentGate>{() => <Outlet />}</DeploymentGate>
      </main>
    </div>
  );
}
